import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, ChevronRight, Phone, Calendar, Search } from 'lucide-react';
import { Link } from 'react-router';
import pristineLogo from '../../assets/pristine Logo.png';
import pristineLogo2 from '../../assets/logo2.png';

const NavBar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const navbarRef = useRef(null);
  const searchInputRef = useRef(null);
  const menuTimeoutRef = useRef(null);
  const submenuTimeoutRef = useRef(null);

  // Brand color constants
  const colors = {
    teal: '#00B5CD', 
    lime: '#A9D239',
    darkGray: '#444444',
    lightTeal: '#E6F7FA',
    lightLime: '#F4F9E5',
  };

  const menuItems = [
    {
      label: 'ABOUT',
      href: '/about',
      dropdownItems: [
        { label: 'MEET THE DOCTOR', href: '/dr-aesha' },
        { label: 'BEFORE AND AFTERS', href: '/before-after' },
        { label: 'CAREERS', href: '/careers' },
        { label: 'FAQ', href: '/faq' },
        { label: 'FEE-FOR-SERVICE DENTIST', href: '/service-fee' },
        { label: 'DENTAL TECHNOLOGY', href: '/technology' },
        { label: 'BLOG', href: '/blog' }
      ]
    },
    {
      label: 'SERVICES',
      href: '/service',
      dropdownItems: [
        { 
          label: 'GENERAL', 
          href: '/general',
          subMenu: [
            { label: 'CLEANINGS & EXAMS', href: '/cleaning_exams' },
            { label: 'EMERGENCY CARE', href: '/emergency_care' },
            { label: 'CROWNS', href: '/crowns' },
            { label: 'GUM THERAPY', href: '/gum_therapy' },
            { label: 'DENTAL FILLINGS', href: '/dental_filling' },
            { label: 'DENTURES', href: '/denture' },
            { label: 'NIGHT GUARDS', href: '/night_guard' },
            { label: 'FAMILY DENTISTRY', href: '/family_dentistry' },
          ]
        },
        { 
          label: 'COSMETIC', 
          href: '/cosmetic',
          subMenu: [
            { label: 'INVISALIGN', href: '/invisalign' },
            { label: 'TEETH WHITENING', href: '/teeth_whitening' },
            { label: 'BONDING', href: '/bonding' },
            { label: 'VENEERS', href: '/veneers' },
            { label: 'SMILE MAKEOVER', href: '/smile_makeover' },
          ]
        },
        { 
          label: 'SURGICAL', 
          href: '/surgical',
          subMenu: [
            { label: 'IMPLANTS', href: '/implants' },
            { label: 'EXTRACTIONS', href: '/extractions' },
            { label: 'ROOT CANAL', href: '/root_canal' },
            { label: 'BONE GRAFTS', href: '/bone_grafts' },
            { label: 'IMPLANT-SUPPORTED DENTURES', href: '/implant_supported_dentures' },
            { label: 'BRIDGES', href: '/bridges' }
          ]
        },
        { 
          label: 'OTHER', 
          href: '/other',
          subMenu: [
            { label: 'CHIPPED OR CRACKED TEETH', href: '/chipped_cracked_teeth' },
            { label: 'WISDOM TOOTH PAIN', href: '/wisdom_tooth_pain' },
            { label: 'MISSING TOOTH', href: '/missing_tooth' },
            { label: 'TOOTHCARE', href: '/toothcare' },
            { label: 'TEETH GRINDING', href: '/teeth_grinding' },
            { label: 'STAINED TEETH', href: '/stained_teeth' },
            { label: 'CROOKED TEETH', href: '/crooked_teeth' },
            { label: 'BLEEDING GUMS', href: '/bleeding_gums' },
            { label: 'BAD BREATH', href: '/bad_breath' }
          ]
        }
      ]
    },
    { label: 'WARRANTY', href: '/warranty' },
    { label: 'NEW PATIENTS', href: '/new-patients' }
  ];

  // Improved function for handling menu hover with delay
  const handleMenuHover = (itemLabel) => {
    // Clear any existing timeout
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
    }
    
    // Set hovered item immediately
    setHoveredItem(itemLabel);
    
    // Reset active submenu when moving to a different parent menu
    if (hoveredItem !== itemLabel) {
      setActiveSubMenu(null);
    }
  };

  // Improved function for handling mouse leave with delay
  const handleMenuLeave = () => {
    // Use timeout to prevent menu from disappearing immediately
    menuTimeoutRef.current = setTimeout(() => {
      if (!activeSubMenu) {
        setHoveredItem(null);
      }
    }, 100);
  };

  // Improved handler for submenu interactions
  const handleSubmenuHover = (itemLabel) => {
    // Clear any existing timeout
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
    
    // Set active submenu immediately
    setActiveSubMenu(itemLabel);
  };

  // Handle submenu leave with delay
  const handleSubmenuLeave = () => {
    // Use timeout to prevent menu from disappearing immediately
    submenuTimeoutRef.current = setTimeout(() => {
      setActiveSubMenu(null);
      setHoveredItem(null);
    }, 100);
  };

  // Clean up timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (menuTimeoutRef.current) {
        clearTimeout(menuTimeoutRef.current);
      }
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
      }
    };
  }, []);

  // Handle scroll event with enhanced scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const scrollThreshold = 40;
      const scrolled = currentScrollPos > scrollThreshold;
      const scrollingDown = currentScrollPos > prevScrollPos && currentScrollPos > 100;
      
      setIsScrolled(scrolled);
      setVisible(!scrollingDown || currentScrollPos < scrollThreshold);
      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevScrollPos]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setActiveDropdown(null);
        setActiveSubMenu(null);
        setIsSearchOpen(false);
        setHoveredItem(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when search is opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <nav 
      ref={navbarRef}
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white shadow-md py-1' : 'bg-transparent py-3'
      } ${
        visible ? 'translate-y-0' : '-translate-y-full'
      }`}
      style={{
        backdropFilter: isScrolled ? 'blur(10px)' : 'none',
        backgroundColor: isScrolled ? 'rgba(255,255,255,0.98)' : 'transparent'
      }}
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo */}
          <div className='logo' style={{width: "100px"}}>
            <Link to=''>
              <img 
                src={isScrolled ? pristineLogo : pristineLogo2} 
                alt="Dental Clinic Logo"
                className={`transition-opacity duration-300 ${
                  isScrolled ? 'opacity-100' : 'opacity-80 hover:opacity-100'
                }`}
              />
            </Link>
          </div>

          {/* Desktop Navigation with improved hover menu system */}
          <div className="hidden lg:flex items-center space-x-4">
            {menuItems.map((item) => {
              // Check if this item should be considered active
              const isItemActive = hoveredItem === item.label;
              
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => handleMenuHover(item.label)}
                  onMouseLeave={handleMenuLeave}
                >
                  <Link
                    to={item.href}
                    className="flex items-center px-3 py-2 rounded-md transition-all duration-300 relative"
                    style={{
                      fontSize: 'smaller', 
                      fontWeight: 'bold',
                      color: isScrolled ? colors.darkGray : 'white',
                    }}
                  >
                    <span className="relative overflow-hidden">
                      {item.label}
                      <span 
                        className={`absolute bottom-0 left-0 h-0.5 transition-all duration-300 ${
                          isItemActive ? 'w-full' : 'w-0'
                        }`}
                        style={{ backgroundColor: isScrolled ? colors.teal : colors.lime }}
                      />
                    </span>
                    {item.dropdownItems && (
                      <ChevronDown 
                        className={`ml-1 w-4 h-4 transition-transform duration-300 ${
                          isItemActive ? 'rotate-180' : ''
                        }`}
                        style={{
                          color: isScrolled ? colors.teal : colors.lime
                        }}
                      />
                    )}
                  </Link>
                  
                  {item.dropdownItems && (
                    <div 
                      className={`absolute top-full left-0 bg-white shadow-lg rounded-md py-2 min-w-[220px] z-50 transform transition-all duration-200 ${
                        isItemActive ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
                      } origin-top`}
                      onMouseEnter={() => handleMenuHover(item.label)}
                      onMouseLeave={handleMenuLeave}
                    >
                      <div className="w-4 h-4 bg-white rotate-45 absolute -top-2 left-6 -z-10 shadow-lg"></div>
                      {item.dropdownItems.map((dropdownItem) => (
                        <div 
                          key={dropdownItem.href} 
                          className="relative"
                          onMouseEnter={() => dropdownItem.subMenu && handleSubmenuHover(dropdownItem.label)}
                          onMouseLeave={() => dropdownItem.subMenu && handleSubmenuLeave()}
                        >
                          <Link
                            to={dropdownItem.href}
                            className="block px-4 py-3 hover:bg-gray-50 text-sm whitespace-nowrap flex items-center justify-between rounded-sm transition-all duration-200 m-1"
                            style={{
                              fontSize: 'smaller', 
                              fontWeight: '500', 
                              color: colors.darkGray,
                            }}
                          >
                            {dropdownItem.label}
                            {dropdownItem.subMenu && (
                              <ChevronRight className="w-4 h-4 ml-8" style={{color: colors.teal}} />
                            )}
                          </Link>
                          
                          {dropdownItem.subMenu && (
                            <div 
                              className={`absolute left-full top-0 bg-white shadow-lg rounded-md py-2 min-w-[240px] transform transition-all duration-200 ${
                                activeSubMenu === dropdownItem.label ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
                              } origin-left`}
                              style={{ marginLeft: '10px' }}
                              onMouseEnter={() => handleSubmenuHover(dropdownItem.label)}
                              onMouseLeave={handleSubmenuLeave}
                            >
                              <div className="w-4 h-4 bg-white rotate-45 absolute top-4 -left-2 -z-10 shadow-lg"></div>
                              <div className="grid grid-cols-1 gap-1 p-1">
                                {dropdownItem.subMenu.map((subItem) => (
                                  <Link
                                    key={subItem.href}
                                    to={subItem.href}
                                    className="block px-4 py-2 hover:bg-gray-50 text-sm whitespace-nowrap rounded-sm transition-all duration-200"
                                    style={{
                                      fontSize: 'smaller', 
                                      fontWeight: '500',
                                      color: colors.darkGray,
                                    }}
                                  >
                                    {subItem.label}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Contact Button Group */}
            <div className="flex items-center space-x-3 ml-6">
              {/* Search Button */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-2 rounded-full transition-all duration-300 hover:bg-opacity-20"
                style={{
                  backgroundColor: isScrolled ? 'rgba(0,181,205,0.1)' : 'rgba(255,255,255,0.1)',
                }}
              >
                <Search className="w-5 h-5" style={{color: isScrolled ? colors.teal : 'white'}} />
              </button>

              {/* Phone Number Button */}
              <a 
                href="tel:8401749379" 
                className="inline-flex items-center px-3 py-2 rounded-md transition-all duration-300 hover:bg-opacity-20"
                style={{
                  fontSize: 'smaller', 
                  fontWeight: 'bold',
                  color: isScrolled ? colors.teal : 'white',
                  backgroundColor: isScrolled ? 'rgba(0,181,205,0.1)' : 'rgba(255,255,255,0.1)',
                }}
              >
                <Phone className="w-4 h-4 mr-2" />
                +91 84017 49379
              </a>
              
              {/* Book Online Button */}
              <Link
                to="/schedule-appointment"
                className="relative inline-flex items-center transition-all rounded-md px-6 py-2 font-bold duration-300 hover:shadow-lg overflow-hidden group"
                style={{
                  fontSize: 'small',
                  backgroundColor: colors.lime,
                  color: colors.darkGray
                }}
              >
                <span className="absolute inset-0 w-0 bg-lime-500 transition-all duration-300 ease-out group-hover:w-full"></span>
                <span className="relative flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  BOOK ONLINE
                </span>
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-3">
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 rounded-full transition-colors duration-300 focus:outline-none"
              style={{
                color: isScrolled ? colors.teal : 'white',
                backgroundColor: isSearchOpen ? (isScrolled ? 'rgba(0,181,205,0.1)' : 'rgba(255,255,255,0.1)') : 'transparent'
              }}
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md transition-all duration-300 focus:outline-none group"
              style={{
                color: isScrolled ? colors.teal : 'white',
                backgroundColor: isMobileMenuOpen ? (isScrolled ? 'rgba(0,181,205,0.1)' : 'rgba(255,255,255,0.1)') : 'transparent'
              }}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <div className="relative w-6 h-6">
                <span 
                  className={`absolute top-0 left-0 w-6 h-0.5 transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2.5' : ''}`}
                  style={{backgroundColor: isScrolled ? colors.teal : 'white'}}
                ></span>
                <span 
                  className={`absolute top-2.5 left-0 w-6 h-0.5 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}
                  style={{backgroundColor: isScrolled ? colors.teal : 'white'}}
                ></span>
                <span 
                  className={`absolute top-5 left-0 w-6 h-0.5 transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2.5' : ''}`}
                  style={{backgroundColor: isScrolled ? colors.teal : 'white'}}
                ></span>
              </div>
            </button>
          </div>
        </div>

        {/* Search Bar Overlay */}
        <div 
          className={`absolute left-0 right-0 transition-all duration-300 overflow-hidden ${
            isSearchOpen ? 'h-16 opacity-100' : 'h-0 opacity-0'
          }`}
          style={{
            backgroundColor: isScrolled ? 'white' : 'rgba(0,0,0,0.85)',
            top: '64px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <div className="relative w-full">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search for services, treatments..."
                className="w-full p-2 pl-10 rounded-md focus:outline-none transition-all duration-300"
                style={{
                  backgroundColor: isScrolled ? colors.lightTeal : 'rgba(255,255,255,0.15)',
                  color: isScrolled ? colors.darkGray : 'white',
                  border: `1px solid ${isScrolled ? colors.teal : 'rgba(255,255,255,0.3)'}`,
                }}
              />
              <Search 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" 
                style={{color: isScrolled ? colors.teal : 'rgba(255,255,255,0.7)'}}
              />
            </div>
          </div>
        </div>

        {/* Mobile Menu - Fixed to properly display content */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden fixed left-0 right-0 bg-white overflow-y-auto z-40 shadow-lg"
            style={{ 
              top: '64px', 
              maxHeight: 'calc(100vh - 64px)',
              visibility: 'visible',
              opacity: 1,
              transform: 'translateY(0)'
            }}
          >
            <div className="p-4 space-y-2">
              {menuItems.map((item) => (
                <div key={item.label} className="rounded-lg overflow-hidden shadow-sm mb-2" style={{
                  border: `1px solid ${colors.lightTeal}`
                }}>
                  <Link
                    to={item.href}
                    onClick={() => {
                      setActiveDropdown(activeDropdown === item.label ? null : item.label)
                      setActiveSubMenu(null);
                      if (!item.dropdownItems) {
                        setIsMobileMenuOpen(false);
                      }
                    }}
                    className="w-full text-left flex justify-between items-center px-4 py-3 hover:bg-gray-50"
                    style={{
                      fontSize: 'smaller',
                      color: colors.darkGray,
                      fontWeight: 'bold',
                      backgroundColor: activeDropdown === item.label ? colors.lightTeal : 'white',
                    }}
                  >
                    {item.label}
                    {item.dropdownItems && (
                      <ChevronDown 
                        className={`w-4 h-4 transform transition-transform duration-300 ${
                          activeDropdown === item.label ? 'rotate-180' : ''
                        }`} 
                        style={{color: colors.teal}}
                      />
                    )}
                  </Link>
                  
                  {item.dropdownItems && activeDropdown === item.label && (
                    <div className="overflow-hidden">
                      {item.dropdownItems.map((dropdownItem) => (
                        <div key={dropdownItem.href} className="border-t border-gray-100">
                          {dropdownItem.subMenu ? (
                            <>
                              <Link
                                to={dropdownItem.href}
                                onClick={() => setActiveSubMenu(activeSubMenu === dropdownItem.label ? null : dropdownItem.label)}
                                className="w-full text-left flex justify-between items-center px-4 py-3 text-sm hover:bg-gray-50"
                                style={{
                                  fontSize: 'smaller',
                                  color: colors.darkGray,
                                  backgroundColor: activeSubMenu === dropdownItem.label ? colors.lightLime : colors.lightTeal,
                                }}
                              >
                                {dropdownItem.label}
                                <ChevronDown 
                                  className={`w-4 h-4 transform transition-transform duration-300 ${
                                    activeSubMenu === dropdownItem.label ? 'rotate-180' : ''
                                  }`} 
                                  style={{color: colors.teal}}
                                />
                              </Link>
                              
                              {activeSubMenu === dropdownItem.label && (
                                <div className="bg-gray-50">
                                  {dropdownItem.subMenu.map((subItem) => (
                                    <Link
                                      key={subItem.href}
                                      to={subItem.href}
                                      className="block px-6 py-2 text-sm border-t border-gray-50 hover:bg-gray-100 transition-colors duration-200"
                                      style={{
                                        color: colors.darkGray,
                                        fontSize: 'smaller',
                                        backgroundColor: colors.lightLime
                                      }}
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      {subItem.label}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <Link
                              to={dropdownItem.href}
                              className="block px-4 py-3 text-sm hover:bg-gray-50"
                              style={{
                                fontSize: 'smaller',
                                color: colors.darkGray,
                                backgroundColor: colors.lightTeal,
                              }}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {dropdownItem.label}
                            </Link>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              <div className="mt-6 grid grid-cols-1 gap-4">
                <a
                  href="tel:8401749379"
                  className="flex items-center justify-center px-4 py-3 rounded-lg transition-all duration-300"
                  style={{
                    fontSize: 'smaller', 
                    color: colors.teal, 
                    fontWeight: 'bold',
                    backgroundColor: colors.lightTeal,
                    border: `1px solid ${colors.teal}`
                  }}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  +91 84017 49379
                </a>
                <Link
                  to="/schedule-appointment"
                  className="flex items-center justify-center text-gray-800 transition-all font-bold rounded-lg px-6 py-3 duration-300"
                  style={{
                    fontSize: 'smaller',
                    backgroundColor: colors.lime,
                    boxShadow: '0 4px 6px -1px rgba(169, 210, 57, 0.4)'
                  }}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  BOOK ONLINE
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }
        @keyframes slideDown {
          from { max-height: 0; }
          to { max-height: 500px; }
        }
        .animate-slideDown {
          animation: slideDown 0.5s ease-in-out;
          overflow: hidden;
        }
      `}</style>
    </nav>
  );
};

export default NavBar;